import React, { useState } from "react";
import { useVault } from "@/contexts/VaultContext";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, ShieldCheck, KeyRound, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only page to rotate the Finance & Payroll vault password.
 * Requires the CURRENT password to prevent a hijacked admin session from
 * silently rotating it without local knowledge.
 */
export default function VaultSettingsPage() {
  const vault = useVault();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && confirm !== next;
  const canSubmit = current && next.length >= 8 && confirm === next && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.put("/vault/password", {
        current_password: current,
        new_password: next,
      });
      toast.success(
        "Vault password updated. Existing unlocked sessions remain valid until expiry.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 max-w-2xl" data-testid="vault-settings-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-amber-100">
          <KeyRound className="h-6 w-6 text-amber-700" />
        </div>
        <div>
          <div className="label-overline">ADMINISTRATOR</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">
            Vault settings
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Rotate the Finance &amp; Payroll second-factor password.
          </p>
        </div>
      </div>

      <Card className="rounded-sm border-amber-200 bg-amber-50/40 mb-4">
        <CardContent className="p-4 text-xs text-amber-900 flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <div>
            Changing this password does <strong>not</strong> revoke
            existing unlocked browser sessions — they remain unlocked until
            their 8-hour cookie expires or you click <strong>Lock now</strong>.
            Share the new password with team members who need access.
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-4">
          <form onSubmit={submit} className="space-y-4">
            <Field label="Current vault password" value={current}
              onChange={setCurrent} testid="vault-current" autoFocus />
            <Field label="New vault password (min 8 chars)" value={next}
              onChange={setNext} testid="vault-new"
              error={tooShort ? "At least 8 characters" : ""} />
            <Field label="Confirm new password" value={confirm}
              onChange={setConfirm} testid="vault-confirm"
              error={mismatch ? "Passwords do not match" : ""} />
            <div className="flex items-center gap-2 pt-2">
              <Button type="submit" disabled={!canSubmit}
                className="rounded-sm" data-testid="vault-save">
                <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
                {busy ? "Saving…" : "Update vault password"}
              </Button>
              {vault.unlocked && (
                <Button type="button" variant="outline" className="rounded-sm"
                  onClick={async () => {
                    await vault.lock();
                    toast.success("Vault locked on this device");
                  }} data-testid="vault-lock-now">
                  <Lock className="h-3.5 w-3.5 mr-1.5" /> Lock now (this device)
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="text-[10px] text-zinc-400 mt-4 leading-relaxed">
        The vault uses a separate bcrypt hash stored in
        <code className="mx-1">app_settings.vault_settings</code> and a
        short-lived JWT cookie (<code>vault_token</code>, 8h TTL).
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, error, autoFocus }) {
  return (
    <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500">
      {label}
      <Input type="password" value={value} autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-10 font-mono text-sm"
        data-testid={testid} />
      {error && (
        <span className="text-[11px] text-red-600 normal-case font-sans tracking-normal mt-1 block">
          {error}
        </span>
      )}
    </label>
  );
}
